import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Logo } from "@/components/intel/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

export const Route = createFileRoute("/reset-password")({
  head: () => ({ meta: [{ title: "Set new password · CARIO INTEL" }] }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Supabase puts the recovery token in the URL hash and emits a
  // PASSWORD_RECOVERY auth event once it's processed. Wait for it before
  // accepting a new password — otherwise updateUser() either fails or, worse,
  // updates the wrong (currently signed-in) account.
  useEffect(() => {
    let cancelled = false;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (cancelled) return;
      if (event === "PASSWORD_RECOVERY") setReady(true);
    });
    // Fallback: if URL has the recovery hash, mark ready immediately
    if (typeof window !== "undefined" && window.location.hash.includes("type=recovery")) {
      setReady(true);
    } else {
      // Last resort: if a session exists already (link auto-consumed), allow
      supabase.auth.getSession().then(({ data }) => { if (data.session) setReady(true); });
    }
    return () => { cancelled = true; subscription.unsubscribe(); };
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (password !== confirm) { setError("Passwords do not match"); return; }
    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      toast.success("Password updated. You're signed in.");
      navigate({ to: "/dashboard" });
    } catch (e: any) {
      setError(e?.message || "Could not update password");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen grid-bg bg-background text-foreground flex items-center justify-center px-4">
      <Toaster />
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <Link to="/login" className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan hover:text-foreground">
            Sign in →
          </Link>
        </div>
        <div className="glass-strong rounded-xl p-8 ring-glow">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">// Set new password</p>
          <h1 className="mt-2 font-display text-3xl">Choose a new password</h1>
          {!ready && (
            <p className="mt-2 text-sm text-muted-foreground">
              Verifying reset link…
            </p>
          )}
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="password">New password</Label>
              <Input id="password" type="password" autoComplete="new-password" required minLength={8}
                value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm">Confirm password</Label>
              <Input id="confirm" type="password" autoComplete="new-password" required minLength={8}
                value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            </div>
            {error && <div className="text-xs text-danger">{error}</div>}
            <Button type="submit" disabled={busy || !ready} className="w-full">
              {busy ? "Updating…" : "Update password"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}